import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Boxes,
  Check,
  ExternalLink,
  Filter,
  LayoutGrid,
  Search,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { TenantSummary } from '@forq/contracts';
import {
  completeCatalog,
  previewDefinition,
  type PreviewGroup,
  type PreviewReadiness,
} from './catalog';
import {
  isProfessionalPreviewModule,
  ProfessionalModulePreview,
} from './ProfessionalModulePreview';

interface CompletePreviewPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
}

interface PreviewModulePageProps extends CompletePreviewPageProps {
  readonly moduleId: string;
}

const groupOrder: readonly PreviewGroup[] = [
  'start',
  'masterdata',
  'operations',
  'commercial',
  'collaboration',
  'system',
];

const groupLabels: Readonly<Record<PreviewGroup, string>> = {
  start: 'Start',
  masterdata: 'Stammdaten',
  operations: 'Operatives Geschäft',
  commercial: 'Kaufmännisch & Vertrieb',
  collaboration: 'Zusammenarbeit & Hilfe',
  system: 'System & Konfiguration',
};

function readinessLabel(readiness: PreviewReadiness): string {
  if (readiness === 'foundation') return 'Modulgrundlage';
  if (readiness === 'platform-preview') return 'Plattform-Vorschau';
  return 'Arbeitsvorschau';
}

function effectiveReadiness(id: string, readiness: PreviewReadiness): PreviewReadiness {
  return ['orders', 'service'].includes(id) ? 'foundation' : readiness;
}

export function CompletePreviewPage({ tenant, mode }: CompletePreviewPageProps) {
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState<PreviewGroup | 'all'>('all');
  const foundationCount = completeCatalog.filter((entry) => (
    effectiveReadiness(entry.id, entry.readiness) === 'foundation'
  )).length;
  const previewCount = completeCatalog.length - foundationCount;
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('de');
    return completeCatalog
      .filter((entry) => group === 'all' || entry.group === group)
      .filter((entry) => !needle || [entry.name, entry.description, groupLabels[entry.group]]
        .some((value) => value.toLocaleLowerCase('de').includes(needle)))
      .sort((left, right) => groupOrder.indexOf(left.group) - groupOrder.indexOf(right.group));
  }, [group, query]);

  return (
    <div className="complete-preview professional-overview">
      <header className="professional-overview__header">
        <div>
          <span>FORQ · Arbeitsumgebung</span>
          <h1>Arbeitsbereiche</h1>
          <p>Module öffnen, den Umsetzungsstand prüfen und die fachlichen Arbeitsabläufe direkt testen.</p>
        </div>
        <div className="professional-overview__context">
          <small>Aktiver Mandant</small>
          <strong>{tenant.name}</strong>
          <span>{mode === 'demo' ? 'Lokale Vorschau' : 'Supabase verbunden'}</span>
        </div>
      </header>

      <section className="professional-overview__status" aria-label="Umsetzungsstand">
        <article><span>Zielbereiche</span><strong>{completeCatalog.length}</strong></article>
        <article><span>Modulgrundlagen</span><strong>{foundationCount}</strong></article>
        <article><span>Arbeitsvorschauen</span><strong>{previewCount}</strong></article>
        <article><span>Mandanten</span><strong>3</strong></article>
        <p><Check size={14} /> FORQ Professional: reduzierte Effekte, höhere Informationsdichte und durchgängige Arbeitsflächen.</p>
      </section>

      <div className="professional-overview__toolbar">
        <label><Search size={14} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Arbeitsbereich suchen" /></label>
        <label><Filter size={14} /><select value={group} onChange={(event) => setGroup(event.target.value as PreviewGroup | 'all')}><option value="all">Alle Bereiche</option>{groupOrder.map((value) => <option value={value} key={value}>{groupLabels[value]}</option>)}</select></label>
        <span>{filtered.length} Treffer</span>
      </div>

      <section className="professional-module-register">
        <div className="professional-module-register__head"><span>Arbeitsbereich</span><span>Bereich</span><span>Umsetzungsstand</span><span>Beschreibung</span><span /></div>
        {filtered.map((entry) => {
          const readiness = effectiveReadiness(entry.id, entry.readiness);
          return (
            <Link to={entry.route} key={entry.id} className="professional-module-register__row">
              <span><i><Boxes size={15} /></i><strong>{entry.name}</strong>{entry.aliases?.length ? <small>{entry.aliases.join(' · ')}</small> : null}</span>
              <span>{groupLabels[entry.group]}</span>
              <span><em className={`is-${readiness}`}>{readinessLabel(readiness)}</em></span>
              <span>{entry.description}</span>
              <span>Öffnen <ArrowRight size={14} /></span>
            </Link>
          );
        })}
      </section>
    </div>
  );
}

export function PreviewModulePage({ moduleId, tenant, mode }: PreviewModulePageProps) {
  const definition = previewDefinition(moduleId);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'worklist' | 'details' | 'history'>('worklist');
  const [message, setMessage] = useState<string | null>(null);

  if (isProfessionalPreviewModule(moduleId)) {
    return <ProfessionalModulePreview moduleId={moduleId} tenant={tenant} mode={mode} />;
  }

  if (!definition) {
    return <div className="complete-preview__missing">Die Vorschau für {moduleId} wurde nicht gefunden.</div>;
  }

  const selected = definition.queue[selectedIndex] ?? definition.queue[0];

  return (
    <div className="module-preview professional-generic-preview">
      <header className="professional-module__header">
        <div>
          <span>{definition.eyebrow}</span>
          <h1>{definition.name}</h1>
          <p>{definition.description}</p>
        </div>
        <div className="professional-module__header-actions">
          <small>{tenant.shortName} · {mode === 'demo' ? 'Vorschau' : 'Supabase'}</small>
          <Link to="/complete-preview"><LayoutGrid size={14} /> Arbeitsbereiche</Link>
          <button type="button" className="is-primary" onClick={() => setMessage(definition.actions[0] ?? 'Aktion gestartet')}>{definition.actions[0] ?? 'Neu'}</button>
        </div>
      </header>

      {/* Unübersehbarer Riegel: Diese Seite zeigt einen ENTWURF, keine Betriebsdaten. Ohne ihn hat
          ein Mitarbeiter „Offene Bedarfe 18" für eine echte Zahl gehalten. */}
      <div className="preview-warning" role="note">
        <AlertTriangle size={18} aria-hidden="true" />
        <div>
          <strong>Geplantes Modul — noch nicht gebaut.</strong>
          <span>
            Alle Kennzahlen und Zeilen auf dieser Seite sind erfundene Beispiele, um den geplanten
            Ablauf zu zeigen. Sie stammen <em>nicht</em> aus den Daten von {tenant.name} und werden
            nirgends gespeichert. Die Knöpfe bewirken nichts.
          </span>
        </div>
      </div>

      {message ? <div className="professional-action-message"><Check size={14} /><span>{message}</span><button type="button" onClick={() => setMessage(null)}>Schließen</button></div> : null}

      <section className="erp-status-strip is-example">
        {definition.metrics.map((metric) => (
          <article key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}<i className="preview-tag">Beispiel</i></strong>
            <small>{metric.note}</small>
          </article>
        ))}
      </section>

      <nav className="erp-module-tabs"><button type="button" className={activeTab === 'worklist' ? 'is-active' : ''} onClick={() => setActiveTab('worklist')}>Arbeitsliste</button><button type="button" className={activeTab === 'details' ? 'is-active' : ''} onClick={() => setActiveTab('details')}>Übersicht</button><button type="button" className={activeTab === 'history' ? 'is-active' : ''} onClick={() => setActiveTab('history')}>Verlauf</button></nav>

      {/* Suche/Filter/Spalten sind hier bewusst gesperrt -- sie haben nie etwas getan. */}
      <div className="erp-toolbar"><label><Search size={14} /><input placeholder={`${definition.name} durchsuchen`} disabled /></label><button type="button" disabled><Filter size={14} /> Filter</button><button type="button" disabled>Spalten</button><span>{definition.queue.length} Beispielzeilen</span></div>

      <div className="erp-split-workspace erp-split-workspace--generic">
        <section className="erp-table-panel">
          <div className="erp-generic-list">
            <div className="erp-generic-list__head"><span>Vorgang</span><span>Status</span><span>Priorität</span><span /></div>
            {definition.queue.map((item, index) => <button type="button" key={item.title} className={selectedIndex === index ? 'is-selected' : ''} onClick={() => setSelectedIndex(index)}><span><strong>{item.title}</strong><small>{item.meta}</small></span><span>{item.status}</span><span><i className={`is-${item.priority}`} />{item.priority === 'critical' ? 'Kritisch' : item.priority === 'attention' ? 'Prüfen' : 'Normal'}</span><ExternalLink size={14} /></button>)}
          </div>
        </section>
        <aside className="erp-detail-panel">
          <div className="erp-detail-panel__heading"><div><span>{definition.name}</span><h2>{selected?.title}</h2></div><em className={`is-${selected?.priority ?? 'normal'}`}>{selected?.status}</em></div>
          <nav>{definition.workflow.map((step, index) => <button type="button" key={step} className={index === 0 ? 'is-active' : ''}>{step}</button>)}</nav>
          {activeTab === 'history' ? <section><span>Letzte Änderung</span><strong>Heute · Vorschauzustand aktualisiert</strong></section> : <dl><div><dt>Beschreibung</dt><dd>{selected?.meta}</dd></div><div><dt>Status</dt><dd>{selected?.status}</dd></div><div><dt>Mandant</dt><dd>{tenant.name}</dd></div><div><dt>Umsetzungsstand</dt><dd>{readinessLabel(definition.readiness)}</dd></div></dl>}
          <div className="erp-detail-panel__actions">{definition.actions.slice(0, 3).map((action, index) => <button type="button" className={index === 0 ? 'is-primary' : ''} key={action} onClick={() => setMessage(action)}>{action}</button>)}</div>
        </aside>
      </div>
    </div>
  );
}
